import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    storage: window.localStorage,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

export interface CompanyDetails {
  id?: string;
  user_id: string;
  company_name: string;
  address: string;
  postcode: string;
  city: string;
  country: string;
  kvk_number: string;
  vat_number: string;
  bank_name: string;
  iban: string;
  bic: string;
  email?: string;
  phone?: string;
  website?: string;
  created_at?: string;
  updated_at?: string;
}

export interface UserFeedback {
  id?: string;
  user_id: string;
  extraction_quality_rating: number;
  missing_features?: string;
  session_info?: {
    files_processed?: number;
    processing_time?: number;
    file_types?: string[];
    [key: string]: any;
  };
  created_at?: string;
  updated_at?: string;
}
  
export const saveCompanyDetails = async (details: Omit<CompanyDetails, 'created_at' | 'updated_at' | 'id'>) => {
  try {
    console.log('Saving company details:', details);

    // Get the current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw sessionError;
    
    if (!session?.user) {
      throw new Error('No authenticated session');
    }

    // First, check if a record exists
    const { data: existingData, error: fetchError } = await supabase
      .from('user_company_details')
      .select('id')
      .eq('user_id', session.user.id)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is the "not found" error code
      throw fetchError;
    }

    let result;
    if (existingData?.id) {
      // Update existing record
      result = await supabase
        .from('user_company_details')
        .update({
          ...details,
          user_id: session.user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingData.id)
        .select();
    } else {
      // Insert new record
      result = await supabase
        .from('user_company_details')
        .insert([{
          ...details,
          user_id: session.user.id
        }])
        .select();
    }

    if (result.error) {
      console.error('Supabase error:', result.error);
      throw result.error;
    }

    console.log('Save successful:', result.data);
    return result.data;
  } catch (error) {
    console.error('Error saving company details:', error);
    throw error;
  }
};

export const getCompanyDetails = async (): Promise<CompanyDetails | null> => {
  try {
    // Get the current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw sessionError;
    
    if (!session?.user) {
      throw new Error('No authenticated session');
    }

    console.log('Fetching company details for user:', session.user.id);

    const { data, error } = await supabase
      .from('user_company_details')
      .select('*')
      .eq('user_id', session.user.id)
      .single();

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    console.log('Fetch successful:', data);
    return data;
  } catch (error) {
    console.error('Error fetching company details:', error);
    throw error;
  }
};

export const saveFeedback = async (feedbackData: {
  extraction_quality_rating: number;
  missing_features?: string;
  session_info?: UserFeedback['session_info'];
}) => {
  try {
    console.log('Saving feedback:', feedbackData);

    // Get the current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw sessionError;
    
    if (!session?.user) {
      throw new Error('No authenticated session');
    }

    // Insert new feedback record
    const { data, error } = await supabase
      .from('user_feedback')
      .insert([{
        user_id: session.user.id,
        extraction_quality_rating: feedbackData.extraction_quality_rating,
        missing_features: feedbackData.missing_features || null,
        session_info: feedbackData.session_info || null
      }])
      .select();

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    console.log('Feedback save successful:', data);
    return data;
  } catch (error) {
    console.error('Error saving feedback:', error);
    throw error;
  }
};

export const getUserFeedback = async (limit?: number): Promise<UserFeedback[]> => {
  try {
    // Get the current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw sessionError;
    
    if (!session?.user) {
      throw new Error('No authenticated session');
    }

    console.log('Fetching feedback for user:', session.user.id);

    let query = supabase
      .from('user_feedback')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    console.log('Feedback fetch successful:', data);
    return data || [];
  } catch (error) {
    console.error('Error fetching feedback:', error);
    throw error;
  }
}; 